// Implements: F-UIX-018, F-CNV-003
/**
 * The usage status of the open mockup (CNV.2, UIX.1): read on opening it and once
 * after each turn settles, and at no other moment.
 *
 * The read count is part of the contract, not an accident of rendering: the per-minute
 * limit (PLT.6) was sized by the T1 measurement on ONE read per opening and ONE per
 * settled turn (S5b), so this hook has no timer, no poll and no read per render. A turn
 * "settles" when `turnOpen` goes from `true` to `false`; any other render reads nothing.
 *
 * Stale answers are ignored by id (checklist 14): an answer counts only if it belongs to
 * the mockup the hook is showing now AND it is the newest read asked for it, so the
 * previous mockup's status never shows on the next one, and an older read that answers
 * last never overwrites a newer one. A failed read shows nothing (no notice is better
 * than a wrong one) and is not repeated.
 */

import { useEffect, useRef, useState } from "react";

import type { UsageStatusView } from "../../../types/api";
import { fetchUsageStatus } from "../api/usageApi";

/** One answer, tagged with the mockup it was read for. */
interface Answer {
  readonly mockupId: string;
  readonly view: UsageStatusView | null;
}

/**
 * @param mockupId The open mockup.
 * @param turnOpen Whether a turn is open on it now; its fall to `false` is a settled turn.
 * @returns The status, or `null` while it is unknown (not read yet, or the read failed).
 */
export function useUsageStatus(mockupId: string, turnOpen: boolean): UsageStatusView | null {
  const [answer, setAnswer] = useState<Answer | null>(null);
  // The newest read asked: its mockup and its number. Anything else that answers is stale.
  const latest = useRef<{ readonly mockupId: string; readonly seq: number }>({ mockupId, seq: 0 });
  // The `turnOpen` of the previous render, to see the edge and only the edge.
  const [wasOpen, setWasOpen] = useState(turnOpen);
  // Bumped once per settled turn: the effect below reads on mount, on a new id and on each bump.
  const [settled, setSettled] = useState(0);

  if (wasOpen !== turnOpen) {
    setWasOpen(turnOpen);
    if (wasOpen) setSettled((count) => count + 1);
  }

  useEffect(() => {
    const mine = { mockupId, seq: latest.current.seq + 1 };
    latest.current = mine;
    const isCurrent = (): boolean =>
      latest.current.mockupId === mine.mockupId && latest.current.seq === mine.seq;
    fetchUsageStatus(mockupId).then(
      (view) => {
        if (isCurrent()) setAnswer({ mockupId, view });
      },
      () => {
        // No notice rather than a wrong one; the next settled turn reads again.
        if (isCurrent()) setAnswer({ mockupId, view: null });
      },
    );
  }, [mockupId, settled]);

  return answer?.mockupId === mockupId ? answer.view : null;
}
